import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  available: { label: 'Disponible', color: 'bg-blue-100 text-blue-700' },
  waiting_part: { label: 'Esperando pieza', color: 'bg-amber-100 text-amber-700' },
  waiting_client: { label: 'Esperando cliente', color: 'bg-purple-100 text-purple-700' },
  outsourcing: { label: 'Servicio externo', color: 'bg-indigo-100 text-indigo-700' },
  liberacion: { label: 'Liberacion', color: 'bg-cyan-100 text-cyan-700' },
  archived: { label: 'Archivado', color: 'bg-slate-100 text-slate-700' },
  done: { label: 'Terminado', color: 'bg-green-100 text-green-700' },
  delivered: { label: 'Entregado', color: 'bg-emerald-100 text-emerald-700' },
};

export default function RepairsList() {
  const { getAuthHeaders, currentBranch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [repairs, setRepairs] = useState([]);
  const [filter, setFilter] = useState('available');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchRepairs(); }, [filter, currentBranch]);

  // Handle incoming client from Clients page
  useEffect(() => {
    if (location.state?.newRepairClient) {
      navigate('/repairs/new', { state: { client: location.state.newRepairClient } });
    }
  }, [location.state]);

  const fetchRepairs = async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.append('status', filter);
      if (search) params.append('search', search);
      if (currentBranch && !search) params.append('branch', currentBranch);
      const res = await axios.get(`${API}/api/repairs?${params}`, { headers: getAuthHeaders() });
      setRepairs(res.data);
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="repairs-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Reparaciones</h1>
        <Button asChild className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="new-repair-button">
          <Link to="/repairs/new"><Plus className="w-4 h-4 mr-2" /> Nueva Reparacion</Link>
        </Button>
      </div>

      {/* Status Filter Tabs — exact order: Disponible, Esperando Pieza,
          Esperando Cliente, Servicio Externo, Liberacion, Terminado,
          Entregado, Archivado, Todos. */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="status-filters">
        {['available', 'waiting_part', 'waiting_client', 'outsourcing', 'liberacion', 'done', 'delivered', 'archived'].map((key) => (
          <button key={key} onClick={() => setFilter(key)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === key ? 'bg-[#1D3C6E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{STATUS_MAP[key]?.label || key}</button>
        ))}
        <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!filter ? 'bg-[#1D3C6E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Todos</button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="repair-search" className="pl-10" placeholder="Buscar por ticket, cliente, telefono o modelo..."
          value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchRepairs()} />
      </div>

      {/* Repairs Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600 whitespace-nowrap" style={{ width: '1%' }}>Bolsa</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 whitespace-nowrap" style={{ width: '1%' }}>Dispositivo</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 whitespace-nowrap" style={{ width: '1%' }}>Cliente</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Descripcion del Problema</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 whitespace-nowrap" style={{ width: '1%' }}>Tiempo de Espera</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 whitespace-nowrap" style={{ width: '1%' }}>Estado</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600 whitespace-nowrap" style={{ width: '1%' }}>Dias</th>
            </tr>
          </thead>
          <tbody>
            {repairs.map((repair) => {
              const createdDate = new Date(repair.created_at);
              const today = new Date();
              const createdDay = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
              const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const daysElapsed = Math.round((todayDay - createdDay) / (1000 * 60 * 60 * 24));
              const to = `/repairs/${repair.id}`;
              const cellCls = 'block px-4 py-3 -mx-4 -my-3 text-inherit no-underline';
              return (
                <tr key={repair.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap" style={{ width: '1%' }}>
                    <Link to={to} className={cellCls}>
                      {repair.bag ? (
                        <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2 rounded-md bg-[#AFBD20]/20 text-[#1D3C6E] font-bold text-sm" data-testid={`repair-bag-${repair.id}`}>
                          #{repair.bag}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap" style={{ width: '1%' }}><Link to={to} className={cellCls}>{repair.device_brand} {repair.device_model}</Link></td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ width: '1%' }}><Link to={to} className={cellCls}>{repair.client_name}</Link></td>
                  <td className="px-4 py-3 text-xs text-slate-600" title={repair.problem_description || ''}><Link to={to} className={cellCls}>{repair.problem_description || '-'}</Link></td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap" style={{ width: '1%' }}><Link to={to} className={cellCls}>{repair.wait_time || '-'}</Link></td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ width: '1%' }}>
                    <Link to={to} className={cellCls}>
                      <span className={`status-badge ${STATUS_MAP[repair.status]?.color || 'bg-slate-100'}`}>{STATUS_MAP[repair.status]?.label || repair.status}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-xs font-medium text-slate-700 whitespace-nowrap" style={{ width: '1%' }} title={createdDate.toLocaleString('es-MX')}><Link to={to} className={cellCls}>{daysElapsed === 0 ? '0' : `${daysElapsed} ${daysElapsed === 1 ? 'dia' : 'dias'}`}</Link></td>
                </tr>
              );
            })}
            {repairs.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No hay reparaciones</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
